import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSessionQCM } from '../../hooks/fetch_api_hook/useSessionQCM';
import { usePostApi } from '../../hooks/post_api_hook/usePostApi';
import { useSessionStream } from '../../hooks/useSessionStream';
import { url } from '../../utils/confserveur';
import ScanMobile from '../../components/ScanMobile/ScanMobile';
import { evaluerReponse, fusionnerReponses, codesNonEnregistres } from './reponsesOptimistes';
import { fluxOuvert } from './revueCorrection';
import { assignationsDepuisParticipants } from './jetonScan';

/**
 * Conteneur d'une session cartes (ArUco) : data, flux SSE, batching des scans
 * et handlers vivent ICI, une seule fois. L'affichage est la vue scan
 * (maquette QFlash). C'est l'écran qui PILOTE la séance : le PC projette, le
 * téléphone décide.
 *
 * Une seule source de données au-delà de la séance elle-même : le FLUX
 * (chantier jeton, 2026-09-04). Les participants (carte → nom) arrivent avec
 * chaque `question_change` ; sous jeton le serveur les a déjà réduits à
 * « Carte N ». Plus de liste d'élèves, de cartes assignées, d'équipes ni de
 * contexte de classe chargés ici : le téléphone relié n'y a pas droit, et le
 * PC n'en avait pas besoin, le flux disait déjà tout.
 *
 * Plus de présence des deux écrans non plus : un téléphone relié par le QR
 * sait que la projection est ouverte, c'est elle qui a fait le jeton.
 *
 * Un seul chemin d'entrée : la page publique `/scan/:id`, qui donne
 * `sessionId` (l'ancien pilotage sous compte a disparu à l'étape 3 du
 * chantier). Ni « Retour » ni « Projeter » : le téléphone n'a pas de tableau
 * de bord. `onPerdu` : le serveur refuse la séance (appareil détaché, cookie
 * expiré) — la page d'entrée affiche « rescannez ».
 */
const SessionLive = ({ sessionId, onPerdu = null }) => {
  const id = sessionId;

  const [refreshKey, setRefreshKey] = useState(0);
  const { data: session, isLoading, isError } = useSessionQCM(id, refreshKey);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const [, doPost] = usePostApi(null, null);

  const [questionCourante, setQuestionCourante] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [reponses, setReponses] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Refus ou silence du serveur sur un geste de pilotage (Suivant, Lancer,
  // Révéler, étape de fin) : DIT au téléphone, jamais avalé (bug de démo du
  // 2026-09-12 : « Suivant ne fait rien », un 401 token_not_valid silencieux).
  const [erreurPilotage, setErreurPilotage] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [scannedCodes, setScannedCodes] = useState(new Set());
  // Dernier événement `etape` reçu par le flux. Le serveur n'émet que les
  // CHANGEMENTS (D14), donc l'étape de naissance ne passe jamais par là : le
  // repli est la charge du GET, comme sur la projection.
  const [etapeFlux, setEtapeFlux] = useState(null);
  // Le scan est ouvert pour UNE question (lot « levez vos cartes »,
  // 2026-09-19) : l'ordre de la question pour laquelle le prof a appuyé sur
  // Scan !, ou que le serveur a nommé dans l'événement `scan_ouvert`. Un ordre,
  // pas un booléen : la question suivante est fermée par construction.
  const [scanOuvertOrdre, setScanOuvertOrdre] = useState(null);
  // Réponses affichées AVANT que le serveur ne les confirme. La tablette connaît
  // déjà la carte et la bonne réponse : elle n'a rien à demander pour afficher
  // la ligne. Le flux SSE ne sert plus qu'à réconcilier.
  const [reponsesOptimistes, setReponsesOptimistes] = useState(new Map());
  const scannedCodesSync = useRef(new Set());
  const pendingScans = useRef([]);
  const batchTimer = useRef(null);

  const statut = session?.statut || 'en_attente';
  const questionActive = session?.question_active || 0;
  const totalQuestions = session?.questions?.length || 0;

  // Le serveur refuse la séance : cookie de scan détaché ou expiré. La page
  // d'entrée reprend la main ; sans elle (ancien chemin), rien à faire de plus.
  const onPerduRef = useRef(onPerdu);
  onPerduRef.current = onPerdu;
  useEffect(() => {
    if (isError) onPerduRef.current?.();
  }, [isError]);

  // Une seule source lue deux fois : le flux quand il a parlé, le GET sinon.
  // Deux replis séparés feraient diverger `etape` et `revue_ordre` (0 est
  // falsy), ce qui casserait la navigation dès la première question.
  const deroule = etapeFlux || session || {};
  const etape = deroule.etape || 'quiz';
  const revueOrdre = deroule.revue_ordre || 0;

  // Carte → nom, tel que le flux l'a dit à la dernière question.
  const assignations = useMemo(() => assignationsDepuisParticipants(participants), [participants]);
  // Carte → disposition des lettres (haut, droite, bas, gauche), envoyée par le
  // flux avec chaque participant : chaque carte a la sienne (anti-copie), le
  // téléphone ne peut traduire une rotation en lettre que grâce à elle.
  const dispositionParCode = useMemo(() => {
    const m = new Map();
    for (const p of participants || []) {
      if (p && p.code_aruco !== undefined && p.code_aruco !== null && typeof p.disposition === 'string') {
        m.set(Number(p.code_aruco), p.disposition);
      }
    }
    return m;
  }, [participants]);

  // La bonne réponse arrive déjà dans `question_change`. Les replis couvrent la
  // question issue du chargement initial de la session, dont la forme diffère —
  // mêmes replis que ProjectionQCM.
  const bonneReponse = questionCourante?.bonne_reponse
    || questionCourante?.contenu?.bonne_reponse
    || questionCourante?.banque_question?.contenu?.bonne_reponse
    || '';

  // Une seule liste pour la vue : le serveur fait foi, l'optimiste ne comble que
  // ce qu'il n'a pas encore renvoyé. Une carte présente des deux côtés n'apparaît
  // donc jamais en double.
  const reponsesAffichees = useMemo(
    () => fusionnerReponses(reponses, reponsesOptimistes),
    [reponses, reponsesOptimistes],
  );

  const oublierOptimistes = useCallback((codes) => {
    if (!codes || codes.length === 0) return;
    setReponsesOptimistes((prev) => {
      const suivant = new Map(prev);
      for (const c of codes) suivant.delete(c);
      return suivant.size === prev.size ? prev : suivant;
    });
  }, []);

  // Le téléphone SUIT la séance rechargée. L'événement `question_change` du
  // flux fait foi quand il arrive, mais s'il est perdu (onglet passé en
  // arrière-plan, connexion coupée une seconde), la séance relue par
  // `refresh()` dit déjà « question active : X+1 » : on y passe sans
  // attendre, avec le même reset que le flux. Avant, la question affichée
  // n'était reprise du GET que si elle était vide : un événement perdu
  // laissait le téléphone sur X pour toujours (« Suivant ne fait rien », et
  // un second appui poussait le serveur à X+2).
  const ordreCourant = questionCourante?.question_ordre ?? questionCourante?.ordre ?? 0;
  useEffect(() => {
    if (questionActive > 0 && questionActive > ordreCourant && session?.questions?.length > 0) {
      const q = session.questions[questionActive - 1];
      if (!q) return;
      setQuestionCourante(q);
      if (ordreCourant > 0) {
        setReponses([]);
        setRevealed(false);
        setScannedCodes(new Set());
        setReponsesOptimistes(new Map());
        scannedCodesSync.current = new Set();
      }
    }
  }, [questionActive, ordreCourant, session]);

  // Retour de l'onglet au premier plan : relire la séance, le flux a pu manquer
  // des événements pendant l'absence (les navigateurs mobiles le coupent).
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  // Flux SSE partagé (hooks/useSessionStream) — la sémantique métier reste ici
  const handleStreamEvent = useCallback((eventType, data) => {
    if (eventType === 'question_change') {
      setQuestionCourante(data);
      setParticipants(data.participants || []);
      setReponses([]);
      setRevealed(false);
      setScannedCodes(new Set());
      setReponsesOptimistes(new Map());
      scannedCodesSync.current = new Set();
    } else if (eventType === 'reponse') {
      if (data.detail) {
        setReponses(data.detail);
      }
    } else if (eventType === 'reveal') {
      setRevealed(true);
    } else if (eventType === 'etape') {
      setEtapeFlux({ etape: data.etape, revue_ordre: data.revue_ordre });
    } else if (eventType === 'session_terminee') {
      // Le serveur ne fait PAS suivre l'étape `fin` par un événement `etape` :
      // il coupe le flux à `session_terminee` (phase 1). Sans cette ligne,
      // `etapeFlux` resterait bloqué sur la dernière étape connue — la revue —
      // et l'écran ne quitterait jamais la correction. `fin`/0 est exactement
      // ce que le serveur vient d'écrire, par `etape/` comme par `end/`.
      setEtapeFlux({ etape: 'fin', revue_ordre: 0 });
    } else if (eventType === 'scan_ouvert') {
      // Ouvert depuis un autre écran (l'onglet de pilotage du PC, un second
      // téléphone) : ce téléphone s'arme aussi. L'ordre est dans l'événement.
      if (data?.question_ordre !== undefined && data?.question_ordre !== null) {
        setScanOuvertOrdre(Number(data.question_ordre));
      }
    }
  }, []);

  // Le flux est ouvert dès `en_attente` : c'est la MÊME connexion qui traverse
  // le lancement — le serveur enchaîne sur `question_change` sans que rien
  // n'ait à être rouvert ici. Une séance `terminee` garde son flux TANT
  // QU'ELLE EST EN REVUE (D11), même règle que la projection.
  // Sur un refus (401/403 : appareil détaché), le hook s'arrête de lui-même et
  // le rafraîchissement ci-dessous fait remonter l'erreur à la page d'entrée.
  useSessionStream({
    sessionId: id,
    enabled: fluxOuvert({ statut, etape }),
    onEvent: handleStreamEvent,
    onTerminated: refresh,
  });

  useEffect(() => {
    if (statut !== 'en_cours') return;
    const handleBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [statut]);

  // Flush des scans pendants au démontage
  useEffect(() => {
    return () => {
      if (batchTimer.current) {
        clearTimeout(batchTimer.current);
        if (pendingScans.current.length > 0) {
          const scans = [...pendingScans.current];
          pendingScans.current = [];
          doPost(scans, `${url}sessions-qcm/${id}/scan-batch/`);
        }
      }
    };
  }, [id, doPost]);

  /**
   * Un geste de pilotage : un délai (8 s, sinon axios attend sans fin et le
   * bouton reste figé), et un refus qui se voit. 401/403 = le cookie du
   * téléphone n'est plus reconnu (jeton expiré, appareil détaché) : la page
   * d'entrée reprend la main et dit de rescanner. Tout autre échec s'affiche
   * sur le téléphone avec la raison du serveur, ou « Pas de réseau ».
   */
  const piloter = async (chemin, charge = {}) => {
    const result = await doPost(charge, `${url}sessions-qcm/${id}/${chemin}/`, { timeoutMs: 8000 });
    if (result?.success) { setErreurPilotage(''); return true; }
    const statutHttp = result?.error?.response?.status;
    if (statutHttp === 401 || statutHttp === 403) {
      onPerduRef.current?.('expire');
      return false;
    }
    const detail = result?.error?.response?.data?.detail || result?.error?.response?.data?.error;
    setErreurPilotage(statutHttp ? `Le serveur a refusé${detail ? ` : ${detail}` : '.'}` : 'Pas de réseau, réessaie.');
    return false;
  };

  const handleReveal = async () => {
    if (revealed) return;
    setRevealed(true);
    await piloter('reveal');
  };

  // Scan ! (lot « levez vos cartes », 2026-09-19) : armé tout de suite, la
  // caméra n'attend pas le serveur ; puis le serveur arbitre et prévient la
  // projection, qui passe à la consigne et aux prénoms en grand. Idempotent
  // côté serveur : un second appui ne rejoue rien. Un refus s'affiche comme
  // pour Suivant ; un appel perdu ne désarme pas, la projection basculera à
  // la première réponse reçue.
  const handleOuvrirScan = async () => {
    setScanOuvertOrdre(ordreCourant);
    await piloter('ouvrir-scan');
  };

  // Ouvert pour la question en cours : par ce téléphone, par le flux, ou déjà
  // ouvert quand la séance est relue (l'instant est porté par la question,
  // `questions[].scan_ouvert_a`, et par la séance pour la question en cours).
  const scanOuvert = scanOuvertOrdre === ordreCourant
    || Boolean(questionCourante?.scan_ouvert_a)
    || (Boolean(session?.scan_ouvert_a) && questionActive === ordreCourant);

  const postScanBatch = async (scans) => {
    const codes = scans.map((s) => s.code_aruco);
    const resultat = await doPost(scans, `${url}sessions-qcm/${id}/scan-batch/`);

    // Envoi perdu : la réponse n'est PAS enregistrée. On retire la ligne et on
    // rouvre le dédoublonnage, sinon l'écran affirme un enregistrement qui n'a
    // pas eu lieu et la carte ne peut plus être rescannée.
    if (!resultat?.success) {
      const statutHttp = resultat?.error?.response?.status;
      if (statutHttp === 401 || statutHttp === 403) onPerduRef.current?.('expire');
      for (const c of codes) scannedCodesSync.current.delete(c);
      setScannedCodes((prev) => {
        const suivant = new Set(prev);
        for (const c of codes) suivant.delete(c);
        return suivant;
      });
      oublierOptimistes(codes);
      return;
    }

    // Le serveur fait foi. Tout ce qu'il n'a pas créé quitte l'affichage
    // optimiste : « already » signifie qu'une réponse existe déjà et qu'elle
    // arrivera par le flux, « error » que la carte n'est pas exploitable.
    oublierOptimistes(codesNonEnregistres(resultat.data));
  };

  const handleStart = async () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    try {
      if (await piloter('start')) {
        await piloter('next_question');
        refresh();
      }
    } finally {
      setIsTransitioning(false);
    }
  };

  const handleNextQuestion = async () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    try {
      if (await piloter('next_question')) refresh();
    } finally {
      setIsTransitioning(false);
    }
  };

  /**
   * Le pilotage du déroulé de fin — podium, revue, clôture — passe par le SEUL
   * `POST /etape/` (phase 2), qui porte la table de transitions et la
   * validation. `end/` n'est plus appelé d'ici : il refuserait de refermer une
   * séance déjà `terminee`, ce que la revue rejouée (D11) demande pourtant.
   *
   * Aucune confirmation, même pour la clôture (chantier jeton, 2026-09-04) :
   * c'est un pas comme les autres, et il se répare, la revue se rejoue depuis
   * le PC avec les résultats intacts.
   */
  const handleEtape = async (charge) => {
    if (isTransitioning || !charge) return;
    setIsTransitioning(true);
    try {
      if (await piloter('etape', charge)) refresh();
    } finally {
      setIsTransitioning(false);
    }
  };

  // Batching des scans (100 ms) — UNIQUE point d'envoi vers scan-batch/,
  // partagé par les deux vues (le scanner ne poste jamais lui-même).
  // Deux formes, poussées telles quelles dans le lot : { code_aruco, rotation }
  // de la caméra (le serveur traduit avec la disposition de la carte), ou
  // { code_aruco, reponse } de la saisie de secours (une lettre lue par le prof).
  const handleScan = (scan) => {
    const { code_aruco, rotation, reponse } = scan;
    if (scannedCodesSync.current.has(code_aruco)) return;
    scannedCodesSync.current.add(code_aruco);
    setScannedCodes(prev => new Set([...prev, code_aruco]));

    // La ligne s'affiche ici, pas au retour du serveur. La lettre optimiste
    // vient de la disposition reçue avec les participants ; sans elle (pas
    // encore arrivés), la ligne est neutre jusqu'à l'événement `reponse` du
    // serveur, qui la remplace. `est_correcte` reste à null si la lettre ou la
    // bonne réponse manque : ni ✓ ni ✗, l'état honnête.
    const lettre = reponse ?? dispositionParCode.get(Number(code_aruco))?.[rotation] ?? null;
    setReponsesOptimistes((prev) => {
      const suivant = new Map(prev);
      suivant.set(code_aruco, {
        code_aruco,
        reponse: lettre,
        est_correcte: evaluerReponse(lettre, bonneReponse),
      });
      return suivant;
    });

    pendingScans.current.push(scan);

    if (!batchTimer.current) {
      batchTimer.current = setTimeout(() => {
        const scans = [...pendingScans.current];
        pendingScans.current = [];
        batchTimer.current = null;
        postScanBatch(scans);
      }, 100);
    }
  };

  if (isLoading || (isError && onPerdu)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Chargement de la session…</p>
      </div>
    );
  }

  return (
    <ScanMobile
      session={session}
      statut={statut}
      etape={etape}
      revueOrdre={revueOrdre}
      questionActive={questionActive}
      totalQuestions={totalQuestions}
      questionCourante={questionCourante}
      reponses={reponsesAffichees}
      revealed={revealed}
      isTransitioning={isTransitioning}
      erreurPilotage={erreurPilotage}
      scannedCodes={scannedCodes}
      assignations={assignations}
      bonneReponse={bonneReponse}
      onScan={handleScan}
      onStart={handleStart}
      onNextQuestion={handleNextQuestion}
      onEtape={handleEtape}
      onReveal={handleReveal}
      scanOuvert={scanOuvert}
      onOuvrirScan={handleOuvrirScan}
    />
  );
};

export default SessionLive;
